import { useEffect, useId, useMemo, useState, type FocusEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faXmark } from '@fortawesome/free-solid-svg-icons';
import { usePrefersReducedMotion } from '@course-authoring/ui-blocks';
import { ModalShell } from '../ModalShell';
import type { CenterstageVideo, LibraryQuery } from '../../api/centerstageApi';
import { useLibrary, useLibraryFilters } from '../../api/centerstageQueries';
import { useAdminNarrowViewport } from '../../admin-ui';
import { CenterstageFilterChips, type CenterstageFilterChip } from '../centerstage/CenterstageFilterChips';
import { CenterstageFindBar } from '../centerstage/CenterstageFindBar';
import { CenterstagePoster, CenterstageVideoGrid } from '../centerstage/CenterstageVideoGrid';
import { PICKER_COPY } from './authorWithAiCopy';
import { createVideoPickerStyles, type VideoPickerStyles } from './videoPickerStyles';
import { VideoPreview } from './VideoPreview';

/**
 * The Author with AI video picker (Sara's in-flow picker, 2026-11-01): the
 * public Centerstage library with the Centerstage page's search and concept
 * autosuggest, concept as the only filter, three columns of cards with a
 * one-click "Select", and a player preview. Any listed video can be picked.
 */

export interface VideoPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onPick: (videoId: string) => void;
  /** Server rejections keyed by video id, shown on that video's card. */
  rowErrors?: Readonly<Record<string, string>>;
  busy?: boolean;
}

export function formatDuration(seconds: number | undefined): string | null {
  if (seconds === undefined || !Number.isFinite(seconds)) return null;
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function VideoPicker({ isOpen, onClose, onPick, rowErrors = {}, busy = false }: VideoPickerProps) {
  const narrow = useAdminNarrowViewport();
  const reducedMotion = usePrefersReducedMotion();
  const styles = useMemo(() => createVideoPickerStyles({ narrow, reducedMotion }), [narrow, reducedMotion]);
  const titleId = useId();
  const [preview, setPreview] = useState<CenterstageVideo | null>(null);

  useEffect(() => {
    if (!isOpen) setPreview(null);
  }, [isOpen]);

  const pick = (videoId: string) => {
    if (busy) return;
    setPreview(null);
    onPick(videoId);
  };

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onClose={onClose}
        ariaLabelledBy={titleId}
        width="860px"
        maxHeight="min(780px, calc(100vh - 56px))"
        centerOnContent
        presentation="admin"
      >
        <div style={styles.picker.header}>
          <div style={styles.picker.headerText}>
            <h2 id={titleId} style={styles.picker.title}>{PICKER_COPY.videoPickerTitle}</h2>
            <p style={styles.picker.description}>{PICKER_COPY.videoPickerDescription}</p>
          </div>
          <button type="button" style={styles.picker.close} onClick={onClose} aria-label={PICKER_COPY.close}>
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" style={{ fontSize: '16px' }} />
          </button>
        </div>
        {/* Mounted only while open, so closing clears the search and concept. */}
        <PickerContent styles={styles} rowErrors={rowErrors} busy={busy} onPreview={setPreview} onPick={pick} />
      </ModalShell>

      {/* A sibling of the picker, not a child, so Escape closes only the preview. */}
      <VideoPreview
        video={preview}
        busy={busy}
        onClose={() => setPreview(null)}
        onUse={pick}
      />
    </>
  );
}

interface PickerContentProps {
  styles: VideoPickerStyles;
  rowErrors: Readonly<Record<string, string>>;
  busy: boolean;
  onPreview: (video: CenterstageVideo) => void;
  onPick: (videoId: string) => void;
}

function PickerContent({ styles, rowErrors, busy, onPreview, onPick }: PickerContentProps) {
  const [draft, setDraft] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [conceptId, setConceptId] = useState<number | null>(null);

  const query: LibraryQuery = useMemo(() => ({
    ...(searchTerm ? { q: searchTerm } : {}),
    ...(conceptId !== null ? { conceptId } : {}),
  }), [searchTerm, conceptId]);
  const filtersQuery = useLibraryFilters();
  const library = useLibrary(query);

  const concepts = useMemo(() => filtersQuery.data?.concepts ?? [], [filtersQuery.data]);
  const categoryNames = useMemo(
    () => new Map((filtersQuery.data?.categories ?? []).map((category) => [category.id, category.name])),
    [filtersQuery.data],
  );
  const conceptName = conceptId !== null ? concepts.find((concept) => concept.id === conceptId)?.name : undefined;
  const chips: CenterstageFilterChip[] = conceptName
    ? [{ key: 'concept', label: conceptName, onRemove: () => setConceptId(null) }]
    : [];

  const videos = useMemo(() => library.data?.pages.flatMap((page) => page.videos) ?? [], [library.data]);
  const gridStatus = library.isPending ? 'pending' : library.isError && !library.data ? 'error' : 'success';

  return (
    <>
      <div style={styles.picker.find}>
        <CenterstageFindBar
          styles={styles.centerstage}
          draft={draft}
          onDraftChange={setDraft}
          searchTerm={searchTerm}
          onCommitSearch={(term) => {
            setSearchTerm(term);
            setDraft(term);
          }}
          concepts={concepts}
          categoryNames={categoryNames}
          onPickConcept={(id) => {
            setDraft('');
            setSearchTerm('');
            setConceptId(id);
          }}
        />
        <CenterstageFilterChips styles={styles.centerstage} chips={chips} />
      </div>
      <div style={styles.picker.body}>
        <CenterstageVideoGrid
          styles={styles.centerstage}
          status={gridStatus}
          videos={videos}
          total={library.data?.pages[0]?.total ?? null}
          hasMore={library.hasNextPage}
          loadingMore={library.isFetchingNextPage}
          loadMoreFailed={library.isFetchNextPageError}
          onShowMore={() => void library.fetchNextPage()}
          onRetry={() => void library.refetch()}
          showCount={false}
          emptyTitle={PICKER_COPY.videoEmptyTitle}
          emptyBody={PICKER_COPY.videoEmptyHint}
          renderCard={(video) => (
            <PickerCard
              styles={styles}
              video={video}
              error={rowErrors[video.id]}
              busy={busy}
              onPreview={onPreview}
              onPick={onPick}
            />
          )}
        />
      </div>
    </>
  );
}

interface PickerCardProps {
  styles: VideoPickerStyles;
  video: CenterstageVideo;
  error?: string;
  busy: boolean;
  onPreview: (video: CenterstageVideo) => void;
  onPick: (videoId: string) => void;
}

/**
 * The still, title and run time. Pointing at or focusing the card dims the
 * still and shows a play button and a "Select" pill; the card opens the
 * preview and "Select" picks the video. The two are sibling buttons, not
 * nested. Select turns magenta only while the pointer is on it.
 */
function PickerCard({ styles, video, error, busy, onPreview, onPick }: PickerCardProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [selectHovered, setSelectHovered] = useState(false);
  const active = hovered || focused;
  const runtime = formatDuration(video.duration);
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
  };

  return (
    <div
      style={{ ...styles.picker.pickCard, ...(active ? styles.picker.pickCardActive : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={onBlur}
    >
      <button
        type="button"
        aria-label={PICKER_COPY.play(video.title)}
        onClick={() => onPreview(video)}
        style={styles.picker.pickCardButton}
      >
        <CenterstagePoster styles={styles.centerstage} video={video}>
          <span aria-hidden="true" style={{ ...styles.picker.thumbScrim, opacity: active ? 1 : 0 }} />
          <span aria-hidden="true" style={{ ...styles.picker.playGlyph, opacity: active ? 1 : 0 }}>
            {/* Nudged 1px right so the triangle looks centered in the circle. */}
            <FontAwesomeIcon icon={faPlay} style={{ marginLeft: '1px' }} />
          </span>
        </CenterstagePoster>
        <span style={styles.picker.pickCardBody}>
          <span style={styles.centerstage.cardTitle}>{video.title}</span>
          {runtime ? <span style={styles.picker.runtime}>{runtime}</span> : null}
        </span>
      </button>
      <button
        type="button"
        aria-label={PICKER_COPY.selectVideo(video.title)}
        onClick={() => onPick(video.id)}
        onMouseEnter={() => setSelectHovered(true)}
        onMouseLeave={() => setSelectHovered(false)}
        disabled={busy}
        style={{
          ...styles.picker.selectPill,
          ...(selectHovered && !busy ? styles.picker.selectPillHover : {}),
          // Stays focusable while hidden, so Tab from the card reaches it (and focusing it shows it).
          opacity: active ? 1 : 0,
          pointerEvents: active ? 'auto' : 'none',
          ...(busy ? { cursor: 'default' } : {}),
        }}
      >
        {PICKER_COPY.select}
      </button>
      {error ? <p role="alert" style={styles.picker.cardError}>{error}</p> : null}
    </div>
  );
}
